import React, { useState, useEffect } from "react";
import { Monitor, Smartphone, X } from "lucide-react";
import { Button } from "./ui/button";

// Warn on anything narrower than a standard laptop (1024px covers tablets & phones)
const MIN_SUPPORTED_WIDTH = 1024;

export default function MobileWarning() {
  // Initialise synchronously so the very first render is already correct and
  // there's no frame where the warning is invisible on a small screen.
  const [isSmallScreen, setIsSmallScreen] = useState(
    () =>
      typeof window !== "undefined" && window.innerWidth < MIN_SUPPORTED_WIDTH,
  );
  // No sessionStorage — warning shows once per page load and reappears on next visit.
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < MIN_SUPPORTED_WIDTH);
    };

    // Sync with any resize that happened before the effect mounted.
    checkScreenSize();

    // Listen for resize events
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    // Intentionally not persisted — warning will reappear on next page load.
  };

  // Don't show if screen is large enough or user dismissed
  if (!isSmallScreen || isDismissed) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-background/95 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 shadow-2xl relative">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <Smartphone className="w-16 h-16 text-muted-foreground/50" />
            <div className="absolute -bottom-1 -right-1 bg-amber-500 rounded-full p-1">
              <X className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold text-foreground">
            Best Viewed on Desktop
          </h2>

          <p className="text-muted-foreground text-sm leading-relaxed">
            My!hū is designed for desktop and tablet devices. For the best
            experience with our interactive maps, data visualizations, and
            environmental monitoring tools, please switch to a larger screen.
          </p>

          <div className="flex items-center justify-center gap-2 py-4 text-primary">
            <Monitor className="w-5 h-5" />
            <span className="text-sm font-medium">
              Recommended: 1024px or wider
            </span>
          </div>

          <div className="pt-2 space-y-3">
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="w-full"
            >
              Continue Anyway
            </Button>

            <p className="text-xs text-muted-foreground">
              Some features may not work correctly on smaller screens
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
